import React, { useState } from "react";

import { Check, ExpandLess, ExpandMore } from "@mui/icons-material";
import HelpCenterOutlinedIcon from "@mui/icons-material/HelpCenterOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Avatar,
  Grid,
} from "@mui/material";

import { getCamelCaseWord } from "../utils/functions";

const CandidateAssessmentUnAttQnaCard = ({ language, translate, qna }) => {
  const isArabic = language === "Arabic"; // Check for Arabic language
  let shouldUseLTR = translate || !isArabic;
  let shouldUseTranslation = translate && !isArabic; // Add this for data selection

  const [expanded, setExpanded] = useState(true);
  const type = qna?.question_category;

  // Parse `answer_text` if it's a stringified array
  const parsedAnswerText = (() => {
    try {
      const sourceText = shouldUseTranslation
        ? qna?.translation?.answer_text
        : qna?.answer_text;
      const parsed = JSON.parse(sourceText);
      return Array.isArray(parsed) ? parsed[0] : sourceText;
    } catch (error) {
      return shouldUseTranslation
        ? qna?.translation?.answer_text
        : qna?.answer_text;
    }
  })();

  const question_text = shouldUseTranslation
    ? qna?.translation?.message
    : qna?.message;
  const expected_answer = shouldUseTranslation
    ? qna?.translation?.expected_answer
    : qna?.expected_answer;
  const options = shouldUseTranslation
    ? qna?.translation?.options
    : qna?.options;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mt: 2,
        bgcolor: "#FFF7F7",
        borderRadius: 2,
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
        border: "1px solid #FFC8C9",
      }}
    >
      {/* Header with Suggested and Rating */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 2,
          justifyContent: "space-between",
          mx: "30px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Avatar sx={{ bgcolor: "black", width: 36, height: 36, mr: 1 }}>
            Q
          </Avatar>
          <Typography
            sx={{
              color: "#333333",
              fontSize: "0.85rem",
              lineHeight: 1.5,
              flexGrow: 1,
              p: 1,
              py: 2,
              bgcolor: "#F9F9F957",
              borderRadius: 2,
              border: "1px solid #F1F1F1",
              fontFamily: "Outfit",
              direction: !shouldUseLTR ? "rtl" : "ltr", // Fixed: use shouldUseLTR
              textAlign: !shouldUseLTR ? "right" : "left", // Fixed: use shouldUseLTR
            }}
          >
            {question_text}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton size="small" sx={{ color: "#666" }}>
              <HelpCenterOutlinedIcon sx={{ fontSize: "1.5rem" }} />
            </IconButton>
            <Typography sx={{ color: "#666", fontSize: "0.875rem" }}>
              {getCamelCaseWord(type)}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", ml: 1 }}>
            <TimerOutlinedIcon
              sx={{ color: "#666", fontSize: "1rem", mr: 0.5 }}
            />
            <Typography
              sx={{ color: "#666", fontSize: "0.875rem" }}
              fontFamily="Outfit"
            >
              1min
            </Typography>
          </Box>
          <IconButton
            size="small"
            sx={{ color: "#666" }}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
      </Box>

      {expanded && (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <Grid
              container
              spacing={4}
              sx={{
                mx: "auto",
              }}
            >
              <Grid item xs={12} sm={12} md={12}>
                {/* Expected Answer */}
                {type && (
                  <>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: "#4CC4FF",
                          width: 36,
                          height: 36,
                          mr: 2,
                        }}
                      >
                        EA
                      </Avatar>
                      {type !== "multiple choice" ? (
                        <Typography
                          sx={{
                            color: "#333333",
                            fontSize: "0.85rem",
                            lineHeight: 1.5,
                            flexGrow: 1,
                            p: 1,
                            py: 2,
                            bgcolor: "#F9F9F957",
                            borderRadius: 2,
                            border: "1px solid #F1F1F1",
                            fontFamily: "Outfit",
                            direction: !shouldUseLTR ? "rtl" : "ltr", // Fixed: use shouldUseLTR
                            textAlign: !shouldUseLTR ? "right" : "left", // Fixed: use shouldUseLTR
                          }}
                        >
                          {expected_answer
                            ? expected_answer
                            : "No answer provided."}
                        </Typography>
                      ) : (
                        <Box
                          sx={{
                            width: "100%",
                            p: 1,
                            py: 2,
                            bgcolor: "#F9F9F957",
                            borderRadius: 2,
                            border: "1px solid #F1F1F1",
                            fontFamily: "Outfit",
                          }}
                        >
                          {options?.map((option, index) => {
                            const letter = String.fromCharCode(65 + index);
                            const isCorrect = expected_answer?.includes(option);

                            return (
                              <Box
                                key={index}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  p: 1,
                                  borderRadius: 1,
                                  mb: 1,
                                  bgcolor: isCorrect
                                    ? "rgba(76, 196, 255, 0.1)"
                                    : "transparent",
                                }}
                              >
                                <Typography
                                  sx={{
                                    minWidth: 30,
                                    fontWeight: 500,
                                    color: "#555",
                                    direction: !shouldUseLTR ? "rtl" : "ltr", // Fixed: use shouldUseLTR
                                    textAlign: !shouldUseLTR ? "right" : "left", // Fixed: use shouldUseLTR
                                    fontSize: "0.85rem",
                                  }}
                                >
                                  {letter}:
                                </Typography>
                                <Typography
                                  sx={{
                                    flex: 1,
                                    color: "#2C3E50",
                                    fontSize: "0.85rem",
                                    direction: !shouldUseLTR ? "rtl" : "ltr", // Fixed: use shouldUseLTR
                                    textAlign: !shouldUseLTR ? "right" : "left", // Fixed: use shouldUseLTR
                                  }}
                                >
                                  {option}
                                </Typography>
                                {isCorrect && (
                                  <Check sx={{ color: "#4CC4FF", ml: 1 }} />
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                  </>
                )}
              </Grid>
            </Grid>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default CandidateAssessmentUnAttQnaCard;
